/**
 * Clipboard/edit commands for the focused contenteditable/textarea editor
 * (CodeMirror plaintext + Milkdown Crepe are both editable).
 *
 * Clipboard READ/WRITE goes through the injected `PlatformBridge` — on desktop
 * (WKWebView) `navigator.clipboard` and `document.execCommand('copy'|'paste')`
 * are unreliable/blocked, so the bridge routes them to a native plugin; on the
 * web the bridge uses `navigator.clipboard` (fine there).
 *
 * EDITABLE-REGION commands (`insertText`, `selectAll`) still use the legacy
 * `document.execCommand` API — those keep working inside editable regions across
 * the supported webviews; only clipboard read/write is blocked. Each call is
 * guarded so an unsupported command is a silent no-op rather than a throw.
 *
 * Because the context menu steals focus when it opens, the focused editor
 * element and selection text are CAPTURED at open time and passed in here; we
 * restore focus to that element before running editable-region commands.
 */

import type { PlatformBridge } from '@slate/core';

/** Re-focus a captured editor element if it's still in the document. */
function refocus(el: Element | null): void {
  if (el instanceof HTMLElement && document.contains(el)) el.focus();
}

/** Run an editable-region execCommand, guarded. Returns false on any failure. */
function exec(command: 'insertText' | 'selectAll', value?: string): boolean {
  try {
    return typeof document.execCommand === 'function'
      ? document.execCommand(command, false, value)
      : false;
  } catch {
    return false;
  }
}

/** Copy the captured selection text to the OS clipboard via the bridge. */
export async function editorCopy(platform: PlatformBridge, text: string): Promise<void> {
  if (!text) return;
  try {
    await platform.writeClipboardText(text);
  } catch {
    /* clipboard write unsupported/denied — no-op */
  }
}

/**
 * Cut: copy the captured selection to the clipboard, then delete it by
 * re-focusing the captured editor element and replacing the selection with an
 * empty string (`insertText` works in editable regions even when copy doesn't).
 */
export async function editorCut(
  platform: PlatformBridge,
  el: Element | null,
  text: string,
): Promise<void> {
  if (!text) return;
  try {
    await platform.writeClipboardText(text);
  } catch {
    /* clipboard write unsupported/denied — leave the selection intact */
    return;
  }
  refocus(el);
  exec('insertText', '');
}

/**
 * Paste: read the clipboard via the bridge, re-focus the captured editor, and
 * insert the text at the selection via `execCommand('insertText')` (the only
 * paste path that works when `execCommand('paste')` is blocked).
 */
export async function editorPaste(platform: PlatformBridge, el: Element | null): Promise<void> {
  let text = '';
  try {
    text = await platform.readClipboardText();
  } catch {
    /* clipboard read unsupported/denied — no-op */
    return;
  }
  if (!text) return;
  refocus(el);
  exec('insertText', text);
}

/** Select all text in the captured editor (works in editable regions). */
export function editorSelectAll(el: Element | null): void {
  refocus(el);
  exec('selectAll');
}
